/**
 * 司机端API配置 - 支持多环境
 * 
 * 环境配置：
 * - 本地开发：使用 localhost:8001
 * - 生产环境：默认使用当前域名下的 /api
 * - 自定义环境：使用 REACT_APP_BACKEND_URL
 * 
 * 功能：
 * - 环境变量配置API URL
 * - Axios拦截器自动处理token验证
 * - Token自动刷新机制（过期前5分钟自动刷新）
 */

import axios from 'axios';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { API_BASE_URL as SHARED_API_BASE_URL } from '../../apiConfig';

// ============================================
// 环境变量配置 API URL
// ============================================
const getApiBaseUrl = () => {
  // 优先使用环境变量
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl) {
    // 确保URL以/api结尾
    return envUrl.endsWith('/api') ? envUrl : `${envUrl}/api`;
  }
  return SHARED_API_BASE_URL;
};

export const API_BASE_URL = getApiBaseUrl();

// 兼容旧代码的别名
export const API = API_BASE_URL;
export const PRODUCTION_API_BASE = API_BASE_URL;

// Token刷新配置
const TOKEN_REFRESH_THRESHOLD_MINUTES = 5; // 过期前5分钟刷新

// ============================================
// 环境检测函数
// ============================================
export function isProductionEnvironment() {
  return !isLocalEnvironment() && !isPreviewEnvironment();
}

export function isPreviewEnvironment() {
  return false;
}

export function isLocalEnvironment() {
  return API_BASE_URL.includes('localhost') || API_BASE_URL.includes('127.0.0.1');
}

// ============================================
// 安全的API请求函数
// ============================================
export function getSafeApiUrl(endpoint) {
  return `${API_BASE_URL}${endpoint}`;
}

export function resolveDriverDownloadUrl(downloadUrl) {
  if (!downloadUrl) {
    return '';
  }
  if (downloadUrl.startsWith('http')) {
    return downloadUrl;
  }

  const apiBase = new URL(API_BASE_URL, window.location.origin);
  return new URL(downloadUrl, apiBase.origin).toString();
}

export async function openDriverPdfDownloadUrl(downloadUrl) {
  const resolvedUrl = resolveDriverDownloadUrl(downloadUrl);
  if (!resolvedUrl) return;
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url: resolvedUrl });
    return;
  }
  window.location.assign(resolvedUrl);
}

// ============================================
// 司机端专用端点
// ============================================
export const DRIVER_ENDPOINTS = {
  login: `${API_BASE_URL}/driver/login`,
  refreshToken: `${API_BASE_URL}/driver/refresh-token`,
  orders: `${API_BASE_URL}/driver/orders`,
  orderDetail: (orderId) => `${API_BASE_URL}/driver/orders/${orderId}`,
  me: `${API_BASE_URL}/driver/me`,
  changePassword: `${API_BASE_URL}/driver/change-password`,
  deleteAccount: `${API_BASE_URL}/driver/delete-account`,
  loginDevices: `${API_BASE_URL}/driver/login-devices`,
  reportEndTime: (orderId) => `${API_BASE_URL}/driver/orders/${orderId}/report-end-time`,
  updateOrderStatus: (orderId) => `${API_BASE_URL}/driver/orders/${orderId}/status`,
  cancelIssueReport: (orderId) => `${API_BASE_URL}/driver/orders/${orderId}/issue-report/cancel`,
  pushTokens: `${API_BASE_URL}/driver/push-tokens`,
  signboardPdf: (orderId) => `${API_BASE_URL}/driver/orders/${orderId}/signboard-pdf`,
  tripChat: (orderId) => `${API_BASE_URL}/driver/orders/${orderId}/chat`,
  chatTemplates: `${API_BASE_URL}/driver/chat/templates`,
  timeOff: `${API_BASE_URL}/driver/time-off`,
};

// ============================================
// JWT Token工具
// ============================================
const JWTUtils = {
  /**
   * 解码JWT token的payload（不验证签名）
   */
  decodePayload(token) {
    try {
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      // Base64 URL decode
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('[JWTUtils] Failed to decode token:', e);
      return null;
    }
  },
  
  /**
   * 获取token过期时间（Unix timestamp）
   */
  getExpiration(token) {
    const payload = this.decodePayload(token);
    return payload?.exp || null;
  },
  
  /**
   * 检查token是否已过期
   */
  isExpired(token) {
    const exp = this.getExpiration(token);
    if (!exp) return true;
    return Date.now() >= exp * 1000;
  },
  
  /**
   * 检查token是否即将过期（在阈值时间内）
   */
  isExpiringSoon(token, thresholdMinutes = TOKEN_REFRESH_THRESHOLD_MINUTES) {
    const exp = this.getExpiration(token);
    if (!exp) return true;
    const thresholdMs = thresholdMinutes * 60 * 1000;
    return Date.now() >= (exp * 1000 - thresholdMs);
  },
  
  /**
   * 获取token剩余有效时间（秒）
   */
  getRemainingTime(token) {
    const exp = this.getExpiration(token);
    if (!exp) return 0;
    const remaining = (exp * 1000 - Date.now()) / 1000;
    return Math.max(0, Math.floor(remaining));
  }
};

// 导出JWTUtils供外部使用
export { JWTUtils };

// ============================================
// Token管理工具
// ============================================
export const TokenManager = {
  TOKEN_KEY: 'driver_token',
  INFO_KEY: 'driver_info',
  
  getToken() {
    return localStorage.getItem(this.TOKEN_KEY);
  },
  
  setToken(token) {
    localStorage.setItem(this.TOKEN_KEY, token);
    // 记录token过期信息
    const exp = JWTUtils.getExpiration(token);
    if (exp) {
      console.log(`[TokenManager] Token stored, expires at: ${new Date(exp * 1000).toISOString()}`);
    }
  },
  
  getDriverInfo() {
    try {
      const info = localStorage.getItem(this.INFO_KEY);
      return info ? JSON.parse(info) : null;
    } catch (e) {
      return null;
    }
  },
  
  setDriverInfo(info) {
    localStorage.setItem(this.INFO_KEY, JSON.stringify(info));
  },
  
  clearAll() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.INFO_KEY);
    console.log('[TokenManager] All driver credentials cleared');
  },
  
  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;
    // 检查token是否已过期
    if (JWTUtils.isExpired(token)) {
      console.log('[TokenManager] Token is expired, clearing credentials');
      this.clearAll();
      return false;
    }
    return !!this.getDriverInfo();
  },
  
  /**
   * 检查token是否需要刷新
   */
  needsRefresh() {
    const token = this.getToken();
    if (!token) return false;
    return JWTUtils.isExpiringSoon(token);
  },
  
  /**
   * 获取token状态信息
   */
  getTokenStatus() {
    const token = this.getToken();
    if (!token) return { valid: false, reason: 'no_token' };
    
    const exp = JWTUtils.getExpiration(token);
    const remainingSeconds = JWTUtils.getRemainingTime(token);
    const isExpired = JWTUtils.isExpired(token);
    const needsRefresh = JWTUtils.isExpiringSoon(token);
    
    return {
      valid: !isExpired,
      expiresAt: exp ? new Date(exp * 1000).toISOString() : null,
      remainingSeconds,
      remainingMinutes: Math.floor(remainingSeconds / 60),
      isExpired,
      needsRefresh
    };
  }
};

// ============================================
// 全局登出处理器 - 将在App中设置
// ============================================
let globalLogoutHandler = null;

export function setGlobalLogoutHandler(handler) {
  globalLogoutHandler = handler;
  console.log('[API Config] Global logout handler registered');
}

function triggerLogout(reason) {
  console.log(`[API Config] Triggering logout: ${reason}`);
  TokenManager.clearAll();
  if (globalLogoutHandler) {
    globalLogoutHandler();
  } else {
    window.location.href = '/driver';
  }
}

// ============================================
// Token刷新管理
// ============================================
let isRefreshing = false;
let refreshPromise = null;

async function refreshToken() {
  // 防止并发刷新
  if (isRefreshing) {
    console.log('[Token Refresh] Already refreshing, waiting...');
    return refreshPromise;
  }
  
  const currentToken = TokenManager.getToken();
  if (!currentToken) {
    throw new Error('No token to refresh');
  }
  
  isRefreshing = true;
  console.log('[Token Refresh] Starting token refresh...');
  
  refreshPromise = (async () => {
    try {
      // 直接使用axios避免触发拦截器的刷新逻辑
      const response = await axios.post(
        `${API_BASE_URL}/driver/refresh-token`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${currentToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );
      
      if (response.data.access_token) {
        TokenManager.setToken(response.data.access_token);
        if (response.data.driver) {
          TokenManager.setDriverInfo(response.data.driver);
        }
        console.log('[Token Refresh] ✅ Token refreshed successfully');
        console.log(`[Token Refresh] New expiration: ${response.data.expires_at}`);
        return response.data.access_token;
      } else {
        throw new Error('No access_token in refresh response');
      }
    } catch (error) {
      console.error('[Token Refresh] ❌ Failed to refresh token:', error.message);
      
      // 如果刷新失败（token已过期或无效），触发登出
      if (error.response?.status === 401) {
        console.log('[Token Refresh] Token expired or invalid, logging out...');
        triggerLogout('Token refresh failed - 401');
      }
      
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  
  return refreshPromise;
}

// ============================================
// 创建带拦截器的Axios实例
// ============================================
export const driverApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// 请求拦截器 - 自动添加token、自动刷新
driverApi.interceptors.request.use(
  async (config) => {
    // 1. 日志记录当前环境
    const fullUrl = config.baseURL + (config.url || '');
    console.log(`[Axios Interceptor] Environment: ${isProductionEnvironment() ? 'PRODUCTION' : isPreviewEnvironment() ? 'PREVIEW' : 'LOCAL'}`);
    
    // 2. 跳过登录和刷新token请求的token处理
    const requestUrl = config.url || '';
    const isAuthRequest = requestUrl.endsWith('/driver/login') ||
      requestUrl === '/driver/login' ||
      requestUrl.includes('/refresh-token');
    
    if (!isAuthRequest) {
      let token = TokenManager.getToken();
      
      // 3. 检查token是否需要刷新
      if (token && TokenManager.needsRefresh()) {
        const status = TokenManager.getTokenStatus();
        console.log(`[Axios Interceptor] Token expiring soon (${status.remainingMinutes} min left), refreshing...`);
        
        try {
          token = await refreshToken();
        } catch (refreshError) {
          console.error('[Axios Interceptor] Token refresh failed:', refreshError.message);
          // 如果刷新失败但token还没过期，继续使用旧token
          if (!JWTUtils.isExpired(token)) {
            console.log('[Axios Interceptor] Using existing token despite refresh failure');
          } else {
            return Promise.reject(refreshError);
          }
        }
      }
      
      // 4. 添加token到请求头
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        const status = TokenManager.getTokenStatus();
        console.log(`[Axios Interceptor] Token attached (${status.remainingMinutes} min remaining)`);
      } else {
        console.warn('[Axios Interceptor] No token found for authenticated request');
      }
    }
    
    console.log(`[Axios Interceptor] Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('[Axios Interceptor] Request error:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器 - 自动处理401错误
driverApi.interceptors.response.use(
  (response) => {
    console.log(`[Axios Interceptor] Response: ${response.status} from ${response.config.url}`);
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url || 'unknown';
    const originalRequest = error.config;
    
    console.error(`[Axios Interceptor] Error: ${status} from ${url}`);
    
    // 处理401未授权错误
    if (status === 401) {
      // 如果是刷新token请求失败，直接登出
      if (url.includes('/refresh-token')) {
        console.error('[Axios Interceptor] Refresh token request failed with 401');
        triggerLogout('Refresh token failed');
        return Promise.reject(error);
      }
      
      // 如果是登录请求，不触发登出
      if (url === '/driver/login' || url.endsWith('/driver/login')) {
        return Promise.reject(error);
      }
      
      // 尝试刷新token并重试请求
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        console.log('[Axios Interceptor] 401 received, attempting token refresh...');
        
        try {
          const newToken = await refreshToken();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return driverApi(originalRequest);
        } catch (refreshError) {
          console.error('[Axios Interceptor] Token refresh failed, logging out');
          triggerLogout('401 Unauthorized after refresh attempt');
          return Promise.reject(refreshError);
        }
      }
      
      // 已经重试过了，登出
      triggerLogout('401 Unauthorized after retry');
    }
    
    // 处理403禁止访问
    if (status === 403) {
      console.error('[Axios Interceptor] 403 Forbidden - Account may be disabled');
    }
    
    return Promise.reject(error);
  }
);

// ============================================
// 便捷API方法
// ============================================
export const DriverAPI = {
  // 登录（不需要token）
  async login(phone, password, deviceInfo = {}) {
    console.log('[DriverAPI] Attempting login...');
    const response = await driverApi.post('/driver/login', {
      phone,
      password,
      device_id: deviceInfo.device_id,
      device_name: deviceInfo.device_name || deviceInfo.model,
      platform: deviceInfo.platform || deviceInfo.operatingSystem || 'web',
      operating_system: deviceInfo.operating_system || deviceInfo.operatingSystem || '',
      app_version: deviceInfo.app_version || '',
    });
    
    if (response.data.access_token) {
      TokenManager.setToken(response.data.access_token);
      TokenManager.setDriverInfo(response.data.driver);
      console.log('[DriverAPI] Login successful, credentials saved');
      
      // 打印token状态
      const status = TokenManager.getTokenStatus();
      console.log(`[DriverAPI] Token valid for ${status.remainingMinutes} minutes`);
    }
    
    return response.data;
  },
  
  // 手动刷新token
  async refreshToken() {
    return await refreshToken();
  },
  
  // 获取token状态
  getTokenStatus() {
    return TokenManager.getTokenStatus();
  },
  
  // 获取订单列表
  async getOrders() {
    const response = await driverApi.get('/driver/orders');
    return response.data;
  },

  async getFleetDrivers() {
    const response = await driverApi.get('/fleet/drivers');
    return response.data;
  },

  async getFleetVehicles() {
    const response = await driverApi.get('/fleet/vehicles');
    return response.data;
  },

  async saveFleetVehicle(vehicle) {
    const response = await driverApi.post('/fleet/vehicles', vehicle);
    return response.data;
  },

  async deleteFleetVehicle(vehicleId) {
    const response = await driverApi.delete(`/fleet/vehicles/${vehicleId}`);
    return response.data;
  },

  async assignFleetOrderDriver(orderId, driverId, vehicleId) {
    const response = await driverApi.post(`/fleet/orders/${orderId}/assign-driver`, {
      driver_id: driverId,
      vehicle_id: vehicleId,
    });
    return response.data;
  },

  async getOrderPool() {
    const response = await driverApi.get('/driver/order-pool');
    return response.data;
  },

  async applyOrderPool(orderId, payload = {}) {
    const response = await driverApi.post(`/driver/order-pool/${orderId}/apply`, payload);
    return response.data;
  },

  async cancelOrderPoolApplication(orderId) {
    const response = await driverApi.post(`/driver/order-pool/${orderId}/cancel-application`);
    return response.data;
  },

  async returnOrderToPool(orderId, payload = {}) {
    const response = await driverApi.post(`/driver/orders/${orderId}/return-to-pool`, payload);
    return response.data;
  },
  
  // 获取订单详情
  async getOrderDetail(orderId) {
    const response = await driverApi.get(`/driver/orders/${orderId}`);
    return response.data;
  },

  async getMonthlyStats(month) {
    const response = await driverApi.get('/driver/monthly-stats', {
      params: { month }
    });
    return response.data;
  },

  async getScoreEvents(limit = 50) {
    const response = await driverApi.get('/driver/score-events', {
      params: { limit }
    });
    return response.data;
  },
  
  // 获取当前司机信息
  async getMe() {
    const response = await driverApi.get('/driver/me');
    return response.data;
  },

  async joinFleet(fleetCode) {
    const response = await driverApi.post('/fleet/join-with-code', { fleet_code: fleetCode });
    return response.data;
  },

  async requestFleetExit(reason) {
    const response = await driverApi.post('/fleet/leave-requests', { reason });
    return response.data;
  },

  async getOnboarding() {
    const response = await driverApi.get('/driver/onboarding');
    return response.data;
  },

  async updateProfilePhoto(profilePhoto) {
    const response = await driverApi.put('/driver/onboarding/profile-photo', { profile_photo: profilePhoto });
    return response.data;
  },

  async getTrainingQuestions() {
    const response = await driverApi.get('/driver/onboarding/training/questions');
    return response.data;
  },

  async getTrainingProgram(language) {
    const response = await driverApi.get('/driver/onboarding/training/program', {
      params: { lang: language }
    });
    return response.data;
  },

  async confirmTrainingPage(payload) {
    const response = await driverApi.post('/driver/onboarding/training/page-confirmation', payload);
    return response.data;
  },

  async getVehicleInspectionOptions() {
    const response = await driverApi.get('/driver/onboarding/vehicle-inspection/options');
    return response.data;
  },

  async submitPersonalInfo(payload) {
    const response = await driverApi.post('/driver/onboarding/personal-info', payload);
    return response.data;
  },

  async submitVehicleInfo(payload) {
    const response = await driverApi.post('/driver/onboarding/vehicle-info', payload);
    return response.data;
  },

  async submitTraining(payload) {
    const response = await driverApi.post('/driver/onboarding/training', payload);
    return response.data;
  },

  async submitVehicleInspection(payload) {
    const response = await driverApi.post('/driver/onboarding/vehicle-inspection', payload);
    return response.data;
  },

  async createVehicleInspectionPayment(payload = {}) {
    const response = await driverApi.post('/driver/onboarding/vehicle-inspection/payment', payload);
    return response.data;
  },

  async getVehicleInspectionPaymentConfig() {
    const response = await driverApi.get('/driver/onboarding/vehicle-inspection/payment-config');
    return response.data;
  },

  async createVehicleInspectionCardPayment(payload) {
    const response = await driverApi.post('/driver/onboarding/vehicle-inspection/card-payment', payload);
    return response.data;
  },

  async submitDocumentManagement(payload) {
    const response = await driverApi.post('/driver/onboarding/document-management', payload);
    return response.data;
  },
  
  // 修改密码
  async changePassword(currentPassword, newPassword) {
    const response = await driverApi.post('/driver/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    if (response.data?.access_token) {
      TokenManager.setToken(response.data.access_token);
    }
    return response.data;
  },

  async deleteAccount(currentPassword, reason = '') {
    const response = await driverApi.post('/driver/delete-account', {
      current_password: currentPassword,
      confirmation: 'DELETE',
      reason,
    });
    return response.data;
  },

  async updateNotificationSettings(payload) {
    const response = await driverApi.post('/driver/notification-settings', payload);
    return response.data;
  },

  async getDriverNotices() {
    const response = await driverApi.get('/driver/notices');
    return response.data;
  },

  async getUnreadDriverNotices() {
    const response = await driverApi.get('/driver/notices/unread');
    return response.data;
  },

  async markDriverNoticeRead(noticeId) {
    const response = await driverApi.post(`/driver/notices/${noticeId}/read`);
    return response.data;
  },

  async getUnreadImportantNotifications() {
    const response = await driverApi.get('/driver/notifications/unread-important');
    return response.data;
  },

  async acknowledgeNotification(notificationId, payload = {}) {
    const response = await driverApi.post(`/driver/notifications/${notificationId}/acknowledge`, payload, { timeout: 10000 });
    return response.data;
  },

  async getLoginDevices() {
    const response = await driverApi.get('/driver/login-devices');
    return response.data;
  },

  async removeLoginDevice(sessionId) {
    const response = await driverApi.delete(`/driver/login-devices/${sessionId}`);
    return response.data;
  },
  
  // 汇报包车结束时间
  async reportEndTime(orderId, endTime) {
    const response = await driverApi.post(`/driver/orders/${orderId}/report-end-time`, {
      end_time: endTime
    });
    return response.data;
  },

  async updateOrderStatus(orderId, status, payload = {}) {
    const response = await driverApi.post(`/driver/orders/${orderId}/status`, { status, ...payload });
    return response.data;
  },

  async cancelIssueReport(orderId) {
    const response = await driverApi.post(`/driver/orders/${orderId}/issue-report/cancel`);
    return response.data;
  },

  async registerPushToken(payload) {
    const response = await driverApi.post('/driver/push-tokens', payload);
    return response.data;
  },

  async markNotificationOpened(notificationId, payload = {}) {
    const response = await driverApi.post(`/driver/notifications/${notificationId}/opened`, payload);
    return response.data;
  },

  async instructionArchive(path = '', params = {}) {
    return (await driverApi.get(`/driver/instruction-archive${path}`, {params, timeout:60000})).data;
  },
  async instructionArchiveLink(id) {
    return (await driverApi.post(`/driver/instruction-archive/${id}/download-link`)).data;
  },
  async getTripInstruction(orderId, sourceType = "official") {
    const resource = sourceType === "driver_extra" ? "extra-schedule" : "orders";
    const response = await driverApi.get(`/driver/${resource}/${orderId}/trip-instruction`, { timeout: 60000 });
    return response.data;
  },

  async createTripInstructionPdfLink(orderId, revision, sourceType = "official") {
    const resource = sourceType === "driver_extra" ? "extra-schedule" : "orders";
    const response = await driverApi.post(`/driver/${resource}/${orderId}/trip-instruction-pdf-link`, null, {
      params: { revision }, timeout: 60000,
    });
    return response.data;
  },

  // 生成举牌 PDF
  async downloadSignboardPdf(orderId) {
    return await driverApi.get(`/driver/orders/${orderId}/signboard-pdf`, {
      responseType: 'blob'
    });
  },

  async createSignboardPdfDownloadLink(orderId) {
    const response = await driverApi.post(`/driver/orders/${orderId}/signboard-pdf-link`);
    return response.data;
  },

  async createExtraSignboardPdfDownloadLink(scheduleId) {
    return (await driverApi.post(`/driver/extra-schedule/${scheduleId}/signboard-pdf-link`)).data;
  },

  async createOrderCalendarDownloadLink(orderId) {
    const response = await driverApi.post(`/driver/orders/${orderId}/calendar-link`);
    return response.data;
  },

  async getTripChat(orderId, language = 'en', options = {}) {
    const response = await driverApi.get(`/driver/orders/${orderId}/chat`, {
      params: {
        lang: language,
        limit: options.limit,
        before_message_id: options.beforeMessageId,
        before_created_at: options.beforeCreatedAt,
        after_message_id: options.afterMessageId,
        after_created_at: options.afterCreatedAt,
      }
    });
    return response.data;
  },

  async sendTripChatMessage(orderId, body, sourceLanguage = 'en', attachments = []) {
    const response = await driverApi.post(`/driver/orders/${orderId}/chat/messages`, {
      body,
      source_language: sourceLanguage,
      attachments
    });
    return response.data;
  },

  async reportTripChatIssue(orderId, issueType, note = '') {
    const response = await driverApi.post(`/driver/orders/${orderId}/chat/issues`, {
      issue_type: issueType,
      note
    });
    return response.data;
  },

  async recallTripChatMessage(orderId, messageId) {
    const response = await driverApi.post(`/driver/orders/${orderId}/chat/messages/${messageId}/recall`);
    return response.data;
  },

  async getChatTemplates(language = 'en') {
    const response = await driverApi.get('/driver/chat/templates', { params: { lang: language } });
    return response.data;
  },

  async createChatTemplate(body) {
    const response = await driverApi.post('/driver/chat/templates', { body });
    return response.data;
  },

  async updateChatTemplate(templateId, body) {
    const response = await driverApi.put(`/driver/chat/templates/${templateId}`, { body });
    return response.data;
  },

  async deleteChatTemplate(templateId) {
    const response = await driverApi.delete(`/driver/chat/templates/${templateId}`);
    return response.data;
  },

  async getTimeOff() {
    const response = await driverApi.get('/driver/time-off');
    return response.data;
  },

  async createTimeOff(data) {
    const response = await driverApi.post('/driver/time-off', data);
    return response.data;
  },

  async cancelTimeOff(timeOffId) {
    const response = await driverApi.delete(`/driver/time-off/${timeOffId}`);
    return response.data;
  },
  
  attendanceRequest: async (method, path, data, params = {}) => (await driverApi.request({ method, url: `/driver/attendance${path}`, data, params })).data,

  // ============= 追加订单 API =============
  getExtraSchedulePreferences: async () => (await driverApi.get('/driver/extra-schedule/preferences')).data,
  saveExtraSchedulePreferences: async (vehicle) => (await driverApi.put('/driver/extra-schedule/preferences', { default_vehicle_type: vehicle })).data,

  // AI识别预约文本
  async parseExtraSchedule(rawText) {
    const response = await driverApi.post('/driver/extra-schedule/parse', { raw_text: rawText, ai_consent: true }, { timeout: 70000 });
    return response.data;
  },
  
  // 创建追加订单
  async createExtraSchedule(data) {
    const response = await driverApi.post('/driver/extra-schedule', data);
    return response.data;
  },
  
  // 获取追加订单列表
  async getExtraSchedules(params = {}) {
    const response = await driverApi.get('/driver/extra-schedule', { params });
    return response.data;
  },
  
  // 获取追加订单详情
  async getExtraScheduleDetail(scheduleId) {
    const response = await driverApi.get(`/driver/extra-schedule/${scheduleId}`);
    return response.data;
  },
  
  // 更新追加订单
  async updateExtraSchedule(scheduleId, data) {
    const response = await driverApi.put(`/driver/extra-schedule/${scheduleId}`, data);
    return response.data;
  },
  
  async updateExtraSettlement(scheduleId, settlementStatus) {
    const response = await driverApi.patch(`/driver/extra-schedule/${scheduleId}/settlement`, { settlement_status: settlementStatus });
    return response.data;
  },

  // 取消追加订单
  async cancelExtraSchedule(scheduleId) {
    const response = await driverApi.delete(`/driver/extra-schedule/${scheduleId}`);
    return response.data;
  },
  
  // 查重检查
  async checkDuplicate(data) {
    const response = await driverApi.post('/driver/extra-schedule/check-duplicate', data);
    return response.data;
  }
};

// ============================================
// 启动时验证配置
// ============================================
console.log('[Driver API Config] ==========================================');
console.log('[Driver API Config] Shared API URL');
console.log('[Driver API Config] API_BASE_URL:', API_BASE_URL);
console.log('[Driver API Config] Environment:', isProductionEnvironment() ? 'PRODUCTION' : isPreviewEnvironment() ? 'PREVIEW' : 'LOCAL');
console.log('[Driver API Config] Token refresh threshold:', TOKEN_REFRESH_THRESHOLD_MINUTES, 'minutes');
console.log('[Driver API Config] Axios interceptors with auto-refresh configured');
console.log('[Driver API Config] ==========================================');

// 启动时检查现有token状态
const existingToken = TokenManager.getToken();
if (existingToken) {
  const status = TokenManager.getTokenStatus();
  console.log('[Driver API Config] Existing token status:', status);
  if (status.isExpired) {
    console.log('[Driver API Config] Existing token is expired, clearing...');
    TokenManager.clearAll();
  } else if (status.needsRefresh) {
    console.log('[Driver API Config] Existing token needs refresh, will refresh on next request');
  }
}
